import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Checkbox,
  IconButton,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@material-ui/core';
import {
  Add,
  Build,
  DeleteForever,
  Edit,
  Settings,
  StarBorder,
  Warning,
} from '@material-ui/icons';
import CustomTable from '../components/CustomTable';
import CustomTooltip from '../../../components/CustomTooltip';
import {
  etichettaCadenza,
  isCadenzaSenzaPeriodicita,
} from '../../../utils/constants';
import { makeStyles } from '@material-ui/styles';
import CategoryDrawer from './CategoryDrawer';
import { isReminderEmpty } from './reminderUtils';
import { labelTipologia } from '../components/TipologiaStrumentazioneModal';

const useStyles = makeStyles((theme) => ({
  controlliWrapper: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginTop: theme.spacing(12),
  },
  controlliHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '90%',
  },
  tableWrapper: {
    width: '90%',
    '& .rt-td': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
  },
  renderValueStyle: {
    display: 'inline-block',
    width: '190px',
    overflow: 'hidden',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    textAlign: 'center',
  },
  customItemWrapper: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
  },
  groupHeader: {
    fontWeight: 600,
    fontSize: '0.8rem',
    opacity: 0.7,
    cursor: 'default',
  },
  categoryButton: {
    border: '2px solid',
    borderColor: theme.palette.primary.main,
    fontWeight: 600,
    '&:hover': {
      border: '2px solid',
    },
  },
  addButton: {
    '&:hover': {
      backgroundColor: theme.palette.primary.main,
    },
  },
  emptyEquipmentText: {
    fontStyle: 'italic',
    color: theme.palette.text.secondary,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.75),
  },
}));

const cadenzeDisponibili = [
  'GIORNALIERA',
  'SETTIMANALE',
  'MENSILE',
  'ANNUALE',
  'ALTRO',
];

const getAssociatedEquipmentIds = (row) => {
  return Array.isArray(row.tipologiaElemento?.attrezzature)
    ? row.tipologiaElemento.attrezzature
    : [];
};

const ControlliConformitaTable = ({
  addControlloToTable,
  categorieControlli,
  deleteRow,
  handleCadenzaChange,
  handleCategoriaControlloChange,
  handleCheckboxChange,
  handleDeleteCategory,
  handleElementoOsservatoChange,
  handleLimitiChange,
  handleTipologiaElementoChange,
  loading,
  openAssociazioneModal,
  openAttrezzaturaModal,
  openLimitiModal,
  openNuovaCategoriaModal,
  openOrarioModal,
  openReminderModal,
  procedura,
  tipologieStrumentazioni,
}) => {
  const classes = useStyles();
  const [drawerOpen, setDrawerOpen] = useState(false);

  const renderEquipmentElementoOsservato = (row) => {
    const associatedEquipmentIds = getAssociatedEquipmentIds(row);

    if (associatedEquipmentIds.length > 0) {
      return (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Build />}
          onClick={() =>
            openAttrezzaturaModal(row.tipologiaElemento?.attrezzature ?? [])
          }
        >
          Vedi Attrezzature
        </Button>
      );
    }

    return (
      <Typography
        variant="body2"
        component="div"
        className={classes.emptyEquipmentText}
        title="Usa la colonna Associazioni per collegare un'attrezzatura"
      >
        <Warning fontSize="small" />
        Nessuna attrezzatura collegata
      </Typography>
    );
  };

  const columns = [
    {
      Header: 'Categoria Controllo',
      id: 'categoriaControllo',
      minWidth: 300,
      accessor: (d) => d,
      Cell: ({ row }) => {
        const currentValue =
          row.categoriaControllo.categoriaControllo || 'CONTROLLO_GENERICO';

        const std = categorieControlli.filter((c) => !c.isCustom);
        const custom = categorieControlli.filter((c) => c.isCustom);

        return (
          <Select
            value={currentValue}
            displayEmpty
            onChange={(event) =>
              handleCategoriaControlloChange(event, row._index)
            }
            renderValue={(selected) => {
              if (selected === 'PERSONALIZZATO') {
                return (
                  <div className={classes.renderValueStyle}>
                    {row.categoriaControllo.categoriaPersonalizzataNome ||
                      'Categoria personalizzata'}
                  </div>
                );
              }

              const item = categorieControlli.find(
                (cat) => cat.name === selected,
              );
              return item ? (
                <div style={{ display: 'flex', justifyContent: 'center' }}>
                  {item.label}
                </div>
              ) : (
                'Seleziona una categoria'
              );
            }}
            style={{ width: '80%' }}
          >
            <MenuItem value="" disabled>
              Seleziona una categoria
            </MenuItem>
            {/* Nasconde "PERSONALIZZATO" dalla lista. Questo serve solo per
            evitare errori di "out-of-range value", ma l'opzione non è
            selezionabile.*/}
            <MenuItem
              value="PERSONALIZZATO"
              disabled
              style={{ display: 'none' }}
            >
              Categoria personalizzata
            </MenuItem>
            <MenuItem disabled className={classes.groupHeader}>
              Standard
            </MenuItem>
            {std
              .filter((cat) => cat.name !== 'PERSONALIZZATO')
              .map((c) => (
                <MenuItem key={c.name} value={c.name}>
                  {c.label}
                </MenuItem>
              ))}
            {custom.length > 0 && (
              <MenuItem disabled className={classes.groupHeader}>
                Personalizzate
              </MenuItem>
            )}
            {custom.map((c) => (
              <MenuItem key={c.id ?? c.name} value={c.name}>
                <StarBorder
                  style={{ fontSize: 16, marginRight: 8, opacity: 0.7 }}
                />
                {c.label}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      Header: 'Tipologia elemento',
      id: 'tipologiaElemento',
      width: 250,
      accessor: (d) => d,
      Cell: ({ row }) => {
        return (
          <Select
            value={row.tipologiaElemento.tipologiaStrumentazioneId ?? ''}
            onChange={(event) =>
              handleTipologiaElementoChange(event, row._index)
            }
            style={{ width: '80%' }}
            displayEmpty
          >
            <MenuItem value={''}>Altra</MenuItem>
            {tipologieStrumentazioni.map((item) => {
              return (
                <MenuItem key={item.id} value={item.id}>
                  {labelTipologia(item.nome)}
                </MenuItem>
              );
            })}
          </Select>
        );
      },
    },
    {
      Header: 'Elemento Osservato',
      id: 'elementiOsservati',
      minWidth: 250,
      accessor: (d) => d,
      Cell: ({ row }) => {
        const [localValue, setLocalValue] = useState(
          row.elementiOsservati.descrizioneOsservabile ?? '',
        );

        return row.tipologiaElemento.tipologiaStrumentazioneId == null ? (
          <TextField
            inputProps={{ style: { textAlign: 'center' } }}
            defaultValue={localValue}
            onChange={(event) => setLocalValue(event.target.value)}
            onBlur={() => handleElementoOsservatoChange(localValue, row._index)}
            style={{
              width: '100%',
            }}
          />
        ) : (
          renderEquipmentElementoOsservato(row)
        );
      },
    },
    {
      // Free-text field: users write descriptions here, not just limits.
      // Backend key stays `limiti`.
      Header: 'Descrizione',
      id: 'limiti',
      minWidth: 210,
      accessor: (d) => d.limiti,
      Cell: ({ row }) => {
        const [localValue, setLocalValue] = useState(row.limiti || '');

        return (
          <TextField
            defaultValue={localValue}
            onChange={(event) => setLocalValue(event.target.value)}
            onBlur={() => handleLimitiChange(localValue, row._index)}
            inputProps={{ style: { textAlign: 'center' } }}
            style={{
              width: '100%',
            }}
          />
        );
      },
    },
    {
      Header: 'Cadenza',
      id: 'cadenza',
      width: 250,
      accessor: (d) => d,
      Cell: ({ row }) => {
        return (
          <Select
            value={row.cadenza.cadenza}
            onChange={(event) => handleCadenzaChange(event, row._index)}
            style={{ width: '80%' }}
          >
            {cadenzeDisponibili?.map((item) => (
              <MenuItem key={item} value={item}>
                {etichettaCadenza(item)}
              </MenuItem>
            ))}
          </Select>
        );
      },
    },
    {
      Header: 'Periodica',
      id: 'periodica',
      width: 100,
      accessor: (d) => d,
      Cell: ({ row }) => {
        return (
          <Checkbox
            checked={Boolean(row.periodica.periodica)}
            onChange={() => handleCheckboxChange(row._index, 'periodica')}
            disabled={
              row.cadenza.cadenza === 0 || row.cadenza.cadenza === 'ALTRO'
            }
          />
        );
      },
    },
    {
      Header: 'Bloccante',
      id: 'bloccante',
      width: 100,
      accessor: (d) => d.bloccante,
      Cell: ({ row }) => {
        // The app re-proposes a control without periodicity as soon as it is
        // registered: there is no next occurrence to schedule, so a blocking
        // one loops and the operator never gets past the blocking list.
        const senzaPeriodicita = isCadenzaSenzaPeriodicita(row.cadenza.cadenza);
        const checkbox = (
          <span>
            <Checkbox
              onChange={() => handleCheckboxChange(row._index, 'bloccante')}
              checked={Boolean(row.bloccante)}
              disabled={senzaPeriodicita}
            />
          </span>
        );

        return senzaPeriodicita ? (
          <CustomTooltip
            title={
              <Typography variant="caption">
                Per rendere bloccante il controllo, scegli una cadenza
                giornaliera, settimanale, mensile o annuale.
              </Typography>
            }
            placement="top"
            arrow
          >
            {checkbox}
          </CustomTooltip>
        ) : (
          checkbox
        );
      },
    },
    {
      Header: 'Lista Orari',
      id: 'listaOrari',
      width: 100,
      accessor: (d) => d,
      Cell: ({ row }) => {
        return (
          <IconButton
            disabled={
              row.periodica.periodica || row.cadenza.cadenza === 'ALTRO'
            }
            onClick={() =>
              openOrarioModal(row.cadenza.cadenza, row.cadenza, row._index)
            }
            size="small"
          >
            <Edit />
          </IconButton>
        );
      },
    },
    {
      Header: 'Conformità e non conformità',
      id: 'isConformita',
      width: 150,
      accessor: (d) => d,
      Cell: ({ row }) => {
        return (
          <IconButton
            onClick={() => {
              openLimitiModal(
                row.isConformita,
                row._index,
                row.categoriaControllo.categoriaControllo,
              );
            }}
            size="small"
          >
            <Edit titleAccess="Modifica limiti e non conformità" />
          </IconButton>
        );
      },
    },
    {
      Header: 'Associazioni',
      id: 'associazioni',
      width: 100,
      accessor: (d) => d,
      Cell: ({ row }) => {
        const associatedEquipmentIds = getAssociatedEquipmentIds(row);
        const isEquipmentControlWithNoAssociatedEquipment =
          row.tipologiaElemento.tipologiaStrumentazioneId != null &&
          associatedEquipmentIds.length === 0;

        return (
          <IconButton
            size="small"
            onClick={() =>
              openAssociazioneModal(
                row.tipologiaElemento.tipologiaStrumentazioneId,
                row.associazioni,
                row._index,
              )
            }
          >
            <Edit
              titleAccess={
                isEquipmentControlWithNoAssociatedEquipment
                  ? 'Attrezzature da associare'
                  : 'Modifica associazioni'
              }
              color={
                isEquipmentControlWithNoAssociatedEquipment
                  ? 'primary'
                  : undefined
              }
            />
          </IconButton>
        );
      },
    },

    {
      Header: 'Promemoria',
      id: 'reminder',
      width: 120,
      accessor: (d) => d,
      Cell: ({ row }) => {
        const { reminderStyle, reminderMessage } = row.reminder;
        const hasReminder = !isReminderEmpty(reminderStyle, reminderMessage);

        return (
          <IconButton
            size="small"
            onClick={() =>
              openReminderModal(reminderStyle, reminderMessage, row._index)
            }
            title={hasReminder ? 'Modifica promemoria' : 'Aggiungi promemoria'}
          >
            <Edit color={hasReminder ? 'primary' : undefined} />
          </IconButton>
        );
      },
    },
    {
      Header: 'Elimina',
      id: 'elimina',
      width: 120,
      accessor: (d) => d,
      Cell: ({ row }) => (
        <IconButton onClick={() => deleteRow(row._index)}>
          <DeleteForever color="primary" />
        </IconButton>
      ),
    },
  ];

  return (
    <div className={classes.controlliWrapper}>
      <div className={classes.controlliHeader}>
        <Typography variant="h4">Controlli di Conformità</Typography>

        <div style={{ display: 'flex', gap: 24, alignItems: 'center' }}>
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setDrawerOpen(true)}
            startIcon={<Settings color="primary" />}
            title="Gestisci categorie personalizzate"
            disabled={loading}
            className={classes.categoryButton}
          >
            Gestisci categorie
          </Button>
          <Button
            variant="contained"
            color="primary"
            startIcon={<Add />}
            onClick={addControlloToTable}
            title="Aggiungi controllo"
            disabled={loading}
            className={classes.addButton}
          >
            Controllo
          </Button>
        </div>
      </div>
      <div className={classes.tableWrapper}>
        <CustomTable
          records={procedura?.controlliConformita || []}
          columns={columns}
          loading={loading}
          filterable={false}
          resizable={true}
          sortable={false}
          defaultPageSize={10}
        />
      </div>
      <CategoryDrawer
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        customCategories={categorieControlli.filter((c) => c.isCustom)}
        onCreate={() => {
          setDrawerOpen(false);
          openNuovaCategoriaModal();
        }}
        onDelete={(cat) => {
          setDrawerOpen(false);
          handleDeleteCategory(cat);
        }}
      />
    </div>
  );
};

ControlliConformitaTable.propTypes = {
  addControlloToTable: PropTypes.func.isRequired,
  categorieControlli: PropTypes.array.isRequired,
  deleteRow: PropTypes.func.isRequired,
  handleCadenzaChange: PropTypes.func.isRequired,
  handleCategoriaControlloChange: PropTypes.func.isRequired,
  handleCheckboxChange: PropTypes.func.isRequired,
  handleDeleteCategory: PropTypes.func.isRequired,
  handleElementoOsservatoChange: PropTypes.func.isRequired,
  handleLimitiChange: PropTypes.func.isRequired,
  handleTipologiaElementoChange: PropTypes.func.isRequired,
  loading: PropTypes.bool.isRequired,
  openAssociazioneModal: PropTypes.func.isRequired,
  openAttrezzaturaModal: PropTypes.func.isRequired,
  openLimitiModal: PropTypes.func.isRequired,
  openNuovaCategoriaModal: PropTypes.func.isRequired,
  openOrarioModal: PropTypes.func.isRequired,
  openReminderModal: PropTypes.func.isRequired,
  procedura: PropTypes.object.isRequired,
  tipologieStrumentazioni: PropTypes.array.isRequired,
};

export default ControlliConformitaTable;
